// Transient, editor-only markup removed on its way to the server. The browser's parser, not
// cheerio: cheerio parses in JavaScript, and one image carrying `data-doc-id` sends the whole
// document through it.
import { parseHtmlFragment } from '@shared/helpers/html.helper';
import { COMMENTS_HTML_ATTRIBUTES, NEW_COMMENT_ID } from '../../comments/constants';
import { IMAGE_HTML_ATTRS } from '../tiptap/nodes/image/constants';
import { ZWSP } from '../tiptap/plugins/zwsp';
import { PULSE_HIGHLIGHT_CLASS } from './scrollContent/pulseHighlight';

const NEW_SELECTION_SELECTOR = [
  `selection-node[${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}="${NEW_COMMENT_ID}"]`,
  `selection-mark[${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}="${NEW_COMMENT_ID}"]`,
].join(', ');

/**
 * Per-session provenance that must not be persisted: image `data-doc-id`, caption ZWSP placeholders,
 * and the scroll-to pulse highlight - it survives the switch from view to edit mode.
 */
const stripTransientProvenance = (root: DocumentFragment): boolean => {
  let changed = false;

  root.querySelectorAll(`mark.${PULSE_HIGHLIGHT_CLASS}`).forEach((mark) => {
    changed = true;
    mark.replaceWith(...Array.from(mark.childNodes));
  });

  root.querySelectorAll(`img[${IMAGE_HTML_ATTRS.DOC_ID}]`).forEach((image) => {
    changed = true;
    image.removeAttribute(IMAGE_HTML_ATTRS.DOC_ID);
  });

  root.querySelectorAll('caption-node').forEach((caption) => {
    const walker = document.createTreeWalker(caption, NodeFilter.SHOW_TEXT);
    const texts: Text[] = [];
    // Collected before mutating: removing a node mid-walk skips the one after it.
    while (walker.nextNode()) texts.push(walker.currentNode as Text);
    texts.forEach((text) => {
      if (!text.data.includes(ZWSP)) return;
      changed = true;
      const cleaned = text.data.split(ZWSP).join('');
      if (cleaned) text.data = cleaned;
      else text.remove();
    });
  });

  return changed;
};

/**
 * Cheap string guards, so a document with nothing transient in it is never parsed at all - which is
 * the common case and must stay free.
 */
const hasTransientProvenance = (html: string): boolean =>
  html.includes(PULSE_HIGHLIGHT_CLASS) ||
  (html.includes('<img') && html.includes(IMAGE_HTML_ATTRS.DOC_ID)) ||
  (html.includes('<caption-node') && html.includes(ZWSP));

const hasNewCommentSelection = (html: string): boolean =>
  html.includes(`${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}="${NEW_COMMENT_ID}"`) ||
  html.includes(`${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}='${NEW_COMMENT_ID}'`);

const serialize = (root: DocumentFragment): string => {
  const holder = document.createElement('template');
  holder.content.appendChild(root);
  return holder.innerHTML;
};

/**
 * Strip transient provenance from a standalone HTML fragment while preserving comment marks, so a
 * comment's serialized field HTML matches what cleanForSave persisted as that field's value.
 */
export const stripTransientProvenanceHtml = (html: string): string => {
  if (!hasTransientProvenance(html)) return html;
  const root = parseHtmlFragment(html);
  return stripTransientProvenance(root) ? serialize(root) : html;
};

/**
 * Clean transient markup before persisting:
 *  - unwrap NEW comment selections
 *  - unwrap the scroll-to pulse highlight
 *  - strip per-session image provenance (data-doc-id)
 *  - remove editor-only ZWSP placeholders from caption content
 */
const cleanForSave = (html: string): string => {
  const hasNewCommentSel = hasNewCommentSelection(html);
  const hasTransient = hasTransientProvenance(html);
  if (!hasNewCommentSel && !hasTransient) return html;

  const root = parseHtmlFragment(html);
  let changed = false;

  if (hasNewCommentSel) {
    root.querySelectorAll(NEW_SELECTION_SELECTOR).forEach((element) => {
      changed = true;
      // Unwrap, not remove - the text a comment was being drawn around is the document's content.
      // The node list is static, so an inner selection is still unwrappable after its parent was:
      // cheerio's `replaceWith(html-string)` detached the inner one and left it in the payload.
      element.replaceWith(...Array.from(element.childNodes));
    });
  }

  if (hasTransient) changed = stripTransientProvenance(root) || changed;

  return changed ? serialize(root) : html;
};

export default cleanForSave;
